/* import __COLOCATED_TEMPLATE__ from './predicates-editor-side-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-fin-qa */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
//@ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    onClose: () => void;
    isOpen: boolean;
    predicateGroup?: $TSFixMe;
    finInvolvedPredicates?: unknown[];
  };
}

export default class PredicatesEditorSideSheet extends Component<Signature> {
  @service declare attributeService: $TSFixMe;
  @service declare appService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare store: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  private get finInvolvedAttribute() {
    return {
      name: this.intl.t('fin-qa-monitor.predicates-editor-side-sheet.fin-involved-attribute-name'),
      identifier: 'conversation.fin_involved',
      type: 'boolean',
      icon: 'fin',
    };
  }

  private get sdrAttributes(): $TSFixMe[] {
    if (!this.appService.app.canUseFeature('fin-sdr-monitors')) {
      return [];
    }
    return [
      {
        name: this.intl.t(
          'fin-qa-monitor.predicates-editor-side-sheet.fin-sales-involved-attribute-name',
        ),
        identifier: 'conversation.fin_sdr_participated',
        type: 'boolean',
        icon: 'fin',
      },
      {
        name: this.intl.t(
          'fin-qa-monitor.predicates-editor-side-sheet.fin-sales-routing-outcome-attribute-name',
        ),
        identifier: 'conversation.fin_sdr_routing_outcome',
        type: 'string',
        icon: 'fin',
      },
      {
        name: this.intl.t(
          'fin-qa-monitor.predicates-editor-side-sheet.fin-sales-qualified-attribute-name',
        ),
        identifier: 'conversation.fin_sdr_sales_qualified',
        type: 'boolean',
        icon: 'fin',
      },
    ];
  }

  private _buildBaseAttributes(): $TSFixMe[] {
    let conversationAttributes = [
      this.finInvolvedAttribute,
      ...this.sdrAttributes,
      ...this.attributeService.assignmentRulesMessageAttributesAsAttributes,
      ...this.attributeService.assigneeAttributes,
      ...this.attributeService.outboundSupportConversationAttributes,
      ...this.attributeService.optionalConversationTagAttribute,
      ...this.attributeService.qaMonitorConversationCustomAttributes,
    ];

    let allAttributes = [
      ...this.attributeService.conversationChannelAttributes,
      ...conversationAttributes,
      ...this.attributeService.filterableUserAttributesForMessaging,
    ];

    if (this.appService.app.companiesActive) {
      allAttributes = allAttributes.concat(
        this.attributeService.filterableCompanyAttributesForMessaging,
      );
    }

    // Deduplicate by identifier, keeping the last occurrence. Attributes from
    // qaMonitorConversationCustomAttributes are appended last, so their custom
    // predicate types (with correct operators/dropdowns) take priority over
    // generic versions of the same attribute from other sources.
    let seen = new Map<string, number>();
    allAttributes.forEach((attr: $TSFixMe, index: number) => {
      if (attr.identifier) {
        seen.set(attr.identifier, index);
      }
    });

    return allAttributes.filter(
      (attr: $TSFixMe, index: number) => !attr.identifier || seen.get(attr.identifier) === index,
    );
  }

  get attributeListOverride() {
    return this._buildBaseAttributes();
  }

  get attributeGroupList() {
    let allAttributes = this._buildBaseAttributes();
    let attributeGroups = this.attributeService.attributesToGroupList(allAttributes);

    if (this.attributeService.ticketAttributesByType?.length > 0) {
      attributeGroups = attributeGroups.concat(this.attributeService.ticketAttributesByType);
    }

    return attributeGroups;
  }

  get predicateContext() {
    return {
      mode: 'rules',
    };
  }

  @action
  onPredicatesUpdate(predicates: $TSFixMe[]) {
    if (!this.args.predicateGroup) {
      return;
    }

    this.args.predicateGroup.set('predicates', copy(predicates));
  }

  @action
  savePredicates() {
    if (!this.args.predicateGroup) {
      return;
    }

    let predicates = this.args.predicateGroup.predicates.serialize() || [];
    let finInvolvedPredicates = this.args.finInvolvedPredicates || [];

    let collapseGuidanceOnly = (preds: $TSFixMe[]): $TSFixMe[] =>
      preds.map((predicate: $TSFixMe) => {
        let collapsed = { ...predicate };
        if (collapsed.predicates) {
          collapsed.predicates = collapseGuidanceOnly(collapsed.predicates);
        }
        if (collapsed.attribute === 'conversation.guidance_only') {
          collapsed.attribute = 'conversation.guidance_applied';
        }
        return collapsed;
      });

    let collapsedPredicates = collapseGuidanceOnly(predicates);

    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-qa-monitor:predicates:updated',
      data: {
        predicates: [...collapsedPredicates, ...finInvolvedPredicates],
      },
    });

    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinQaMonitor::PredicatesEditorSideSheet': typeof PredicatesEditorSideSheet;
    'fin-qa-monitor/predicates-editor-side-sheet': typeof PredicatesEditorSideSheet;
  }
}
